import React from 'react';
import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

type AllergiesMultiSelectProps = {
  /**
   * The array of selected allergens (enum string values).
   */
  value: string[];
  /**
   * Handler that receives updated list of selected allergens.
   */
  onChange: (newValue: string[]) => void;
};

/**
 * A fixed list of allergens pulled from your Kotlin enum:
 * enum class Allergy { GLUTEN, SHELLFISH, EGGS, FISH, ... }
 * We just hard-code them here as the problem states.
 */
const ALLERGENS = [
  'GLUTEN',
  'SHELLFISH',
  'EGGS',
  'FISH',
  'PEANUTS',
  'SOY',
  'MILK',
  'NUTS',
  'CELERY',
  'MUSTARD',
  'SESAME',
  'SULFITE',
  'LUPINUS',
  'MOLLUSCS',
];

export function AllergiesMultiSelect({
  value,
  onChange,
}: AllergiesMultiSelectProps) {
  const { t } = useTranslation();

  const handleSelectChange = (event: SelectChangeEvent<typeof value>) => {
    // `event.target.value` will be a string or string[] depending on MUI config
    // so we cast it to a string[] here.
    const newValue = typeof event.target.value === 'string'
      ? event.target.value.split(',')
      : event.target.value;

    onChange(newValue);
  };

  return (
    <FormControl fullWidth>
      <InputLabel id="allergies-label">{t('labels.allergies')}</InputLabel>
      <Select
        labelId="allergies-label"
        multiple
        value={value}
        onChange={handleSelectChange}
        label={t('labels.allergies')}
        renderValue={(selected) =>
          (selected as string[])
            .map((allergen) => t(`allergens.${allergen}`))
            .join(', ')
        }
      >
        {ALLERGENS.map((allergen) => {
          const isChecked = value.includes(allergen);
          return (
            <MenuItem key={allergen} value={allergen}>
              <Checkbox checked={isChecked} />
              <ListItemText primary={t(`allergens.${allergen}`)} />
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
}
